import clsx from 'clsx';
import { Pill } from '../../../layout/Pill.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { printBarcode } from '../../../shared/printBarcode.js';

const TYPE_LABELS = {
  sellable: 'قابل للبيع',
  consumable: 'استهلاكي',
  service: 'خدمة',
  gift: 'هدية'
};

function StatusPill({ product }) {
  if (!product.isActive) return <Pill variant="muted">معطّل</Pill>;
  // البنود بلا مخزون ليست «منخفضة» أبداً — رصيدها صفر بطبيعتها.
  if (product.tracksStock === false) return <Pill variant="success">نشط</Pill>;
  if (product.lowStock || (product.currentStock <= product.minStockAlert)) {
    return <Pill variant="danger">منخفض</Pill>;
  }
  return <Pill variant="success">نشط</Pill>;
}

function MarginPill({ cost, sale }) {
  if (!cost || cost <= 0) return <Pill variant="muted">—</Pill>;
  const m = Math.round(((sale - cost) / cost) * 100);
  return (
    <Pill variant={m > 50 ? 'success' : m > 20 ? 'warning' : 'danger'}>
      {m}%
    </Pill>
  );
}

export function ProductsTable({
  products = [],
  loading,
  page,
  pages,
  total,
  onPageChange,
  onEdit,
  onDelete,
  canEdit
}) {
  return (
    <div className="card">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-ink-2 text-[12px] sticky top-0 bg-white">
            <tr className="border-b border-line">
              <th className="p-2.5 text-start">المنتج</th>
              <th className="p-2.5 text-start">SKU</th>
              <th className="p-2.5 text-start">الباركود</th>
              <th className="p-2.5 text-start">القسم</th>
              <th className="p-2.5 text-start">المخزن</th>
              <th className="p-2.5 text-start">النوع</th>
              <th className="p-2.5 text-start">الكلفة</th>
              <th className="p-2.5 text-start">سعر البيع</th>
              <th className="p-2.5 text-start">الهامش</th>
              <th className="p-2.5 text-start">المخزون</th>
              <th className="p-2.5 text-start">الحد الأدنى</th>
              <th className="p-2.5 text-start">الحالة</th>
              {canEdit ? <th className="p-2.5 text-end">إجراءات</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canEdit ? 13 : 12} className="text-center text-ink-3 py-8">جاري التحميل...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={canEdit ? 13 : 12} className="text-center text-ink-3 py-10">لا توجد منتجات مطابقة للبحث</td></tr>
            ) : (
              products.map((p, idx) => (
                <tr
                  key={p._id || p.id}
                  className={clsx('border-b border-line hover:bg-paper-2', idx % 2 === 1 && 'bg-paper/40')}
                >
                  <td className="p-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[22px]">{p.emoji || '📦'}</span>
                      <span className="font-bold">{p.name}</span>
                    </div>
                  </td>
                  <td className="p-2.5 text-ink-2" style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.sku}</td>
                  <td className="p-2.5" style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.barcode || '—'}</td>
                  <td className="p-2.5">
                    {p.categoryId?.name ? (
                      <Pill variant="info">{p.categoryId.name}</Pill>
                    ) : '—'}
                  </td>
                  <td className="p-2.5 text-ink-2">{p.warehouseId?.name || '—'}</td>
                  <td className="p-2.5">
                    <Pill variant="muted">{TYPE_LABELS[p.type] || p.type}</Pill>
                  </td>
                  <td className="p-2.5">{formatIQD(p.costPrice)}</td>
                  <td className="p-2.5 font-bold">{formatIQD(p.salePrice)}</td>
                  <td className="p-2.5">
                    <MarginPill cost={p.costPrice} sale={p.salePrice} />
                  </td>
                  <td className="p-2.5 font-bold">
                    {p.tracksStock === false
                      ? <span className="text-ink-3 font-normal text-[12px]">بدون مخزون</span>
                      : (p.currentStock ?? 0)}
                  </td>
                  <td className="p-2.5 text-ink-2">{p.minStockAlert ?? 0}</td>
                  <td className="p-2.5"><StatusPill product={p} /></td>
                  {canEdit ? (
                    <td className="p-2.5">
                      <div className="flex gap-1 justify-end">
                        {p.barcode ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 8px' }}
                            onClick={() =>
                              printBarcode({
                                value: p.barcode,
                                label: p.name,
                                sku: p.sku,
                                price: Number(p.salePrice) || 0
                              })
                            }
                            title="طباعة الباركود"
                          >
                            <Icon name="print" size={13} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: '4px 8px' }}
                          onClick={() => onEdit(p)}
                          title="تعديل"
                        >
                          <Icon name="edit" size={13} />
                        </button>
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: '4px 8px', color: '#B91C1C' }}
                          onClick={() => onDelete(p)}
                          title="حذف"
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 ? (
        <div className="card-h border-b-0 border-t flex justify-between items-center text-[12px]">
          <div className="text-ink-3">
            صفحة {page} من {pages} • {total} منتج
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              className="btn"
              style={{ padding: '6px 10px' }}
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <Icon name="chevronR" size={13} />
            </button>
            <button
              type="button"
              className="btn"
              style={{ padding: '6px 10px' }}
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages}
            >
              <Icon name="chevronL" size={13} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
